/**
 * @file index.tsx
 *
 * @fileoverview Renders the job form publishing date input.
 */
import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { AppContext } from '~/jobiqo/context/app';
import { AppContextInterface } from '~/jobiqo/models/app';
import { format } from 'date-fns';
import { deriveDateFormat } from '~/jobiqo/utils/dates';
import { Controller, useFormContext } from 'react-hook-form';
import { formatJobDate } from '~/jobiqo/services/jobs';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { FormDescription } from '~/vcl/atoms/01-forms/11-form-item/02-form-description';
import { SingleDatePopup } from '~/vcl/atoms/01-forms/06-date/single';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';

export const FIELD_JOB_PUBLISHING_DATE = 'field_job_publishing_date';
// The default value for publishing date is today - business logic.
export const DEFAULT_JOB_PUBLISHING_DATE = new Date();
interface JobFormPublishingDateInputProps {
  /**
   * The read-only flag.
   */
  isReadOnly: boolean;
  /**
   * The job publishing date value.
   */
  value?: string;
}

export interface JobFormPublishingDateFormField {
  /**
   * The job publishing date form field.
   */
  [FIELD_JOB_PUBLISHING_DATE]: string;
}

export function JobFormPublishingDateInput({
  isReadOnly,
  value,
}: JobFormPublishingDateInputProps) {
  const intl = useIntl();
  const { locale }: AppContextInterface = useContext(AppContext);
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();
  const dateFormat: string = deriveDateFormat(intl);

  const label = intl.formatMessage({
    id: 'job_publishing_date_label',
    defaultMessage: 'Publishing date',
  });

  useEffect(() => {
    // Set initial value in the form context so
    // we can calculate expiration datetime.
    if (value && !isReadOnly) {
      setValue(
        FIELD_JOB_PUBLISHING_DATE,
        format(new Date(value), 'yyyy-MM-dd'),
      );
    }
  }, [value]);

  return isReadOnly ? (
    <>
      <Controller
        name={FIELD_JOB_PUBLISHING_DATE}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            error={!!error}
            id={FIELD_JOB_PUBLISHING_DATE}
            label={label}
            type="text"
            disabled={true}
            value={formatJobDate(value, intl)}
          />
        )}
      />
      <FormDescription>
        {intl.formatMessage({
          id: 'job_publishing_date_description_read_only',
          defaultMessage:
            'You cannot change the publishing date for an already published job.',
        })}
      </FormDescription>
    </>
  ) : (
    <>
      <Controller
        name={FIELD_JOB_PUBLISHING_DATE}
        control={control}
        render={({ fieldState: { error } }) => (
          <SingleDatePopup
            id={FIELD_JOB_PUBLISHING_DATE}
            minDate={new Date()}
            label={label}
            description={intl.formatMessage({
              id: 'job_publishing_date_description',
              defaultMessage:
                'Please set the date when the job should be published (note: if the review needs some time the job will be published as soon as possible)',
            })}
            placeholder={intl.formatMessage({
              id: 'job_publishing_date_placeholder',
              defaultMessage: 'Date',
            })}
            error={!!error}
            initialDate={value ? new Date(value) : DEFAULT_JOB_PUBLISHING_DATE}
            locale={locale}
            readOnly={isReadOnly}
            displayFormat={dateFormat}
            onDateChange={(date) => {
              if (date) {
                setValue(FIELD_JOB_PUBLISHING_DATE, format(date, 'yyyy-MM-dd'));
              }
            }}
          />
        )}
      />

      {errors[FIELD_JOB_PUBLISHING_DATE] && !isReadOnly && (
        <InlineError
          message={errors[FIELD_JOB_PUBLISHING_DATE]?.message as string}
        />
      )}
    </>
  );
}

export default JobFormPublishingDateInput;
